import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, PlusCircle, User, Menu, X, ChevronDown, ShieldCheck, LogIn, UserCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import LocationPicker from "@/components/common/LocationPicker";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export default function Header() {
  const navigate = useNavigate();
  const { favorites, categories, adminLoggedIn, user, logout } = useApp();
  const [q, setQ] = useState("");
  const [county, setCounty] = useState("");
  const [city, setCity] = useState("");
  const [locOpen, setLocOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const locLabel = city || county || "Toată țara";

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (county) params.set("county", county);
    if (city) params.set("city", city);
    navigate(`/cauta?${params.toString()}`);
  };

  const onPostAd = () => navigate(user ? "/adauga-anunt" : "/login", { state: !user ? { next: "/adauga-anunt" } : undefined });

  return (
    <header className="sticky top-0 z-50" style={{ background: "var(--amd-header-bg)", borderBottom: "1px solid var(--amd-border)" }}>
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-3 flex items-center gap-3">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 rounded flex items-center justify-center font-black text-white text-sm" style={{ background: "var(--amd-primary)" }}>64</div>
          <span className="text-white font-black tracking-tight text-xl hidden sm:block">amd<span style={{ color: "var(--amd-primary)" }}>64</span>.ro</span>
        </Link>

        <form onSubmit={submit} className="hidden md:flex flex-1 items-stretch h-11 rounded-md overflow-hidden" style={{ background: "var(--amd-surface-alt)", border: "1px solid var(--amd-border)" }}>
          <div className="flex items-center gap-2 px-3 flex-1">
            <Search className="w-4 h-4" style={{ color: "var(--amd-text-muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ce cauți?" className="w-full bg-transparent border-0 outline-none text-sm text-white placeholder:text-neutral-500" />
          </div>
          <div className="hidden lg:flex items-center border-l" style={{ borderColor: "var(--amd-border)" }}>
            <Popover open={locOpen} onOpenChange={setLocOpen}>
              <PopoverTrigger asChild>
                <button type="button" data-testid="header-location-trigger" className="flex items-center gap-2 px-3 text-sm h-full hover:text-white max-w-[200px]" style={{ color: "var(--amd-text-muted)" }}>
                  <span className="truncate">{locLabel}</span> <ChevronDown className="w-4 h-4 shrink-0" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-3 space-y-2">
                <div className="text-xs uppercase tracking-wider font-bold" style={{ color: "var(--amd-text-muted)" }}>Alege locația</div>
                <LocationPicker
                  county={county}
                  city={city}
                  onChange={(c, ci) => { setCounty(c); setCity(ci); }}
                  searchMode
                  className="grid grid-cols-1 gap-2"
                  testIdPrefix="header"
                />
                <button type="button" data-testid="header-location-reset" onClick={() => { setCounty(""); setCity(""); }} className="text-xs amd-link">Resetează</button>
              </PopoverContent>
            </Popover>
          </div>
          <button type="submit" className="px-5 font-semibold text-white text-sm amd-btn-primary">Caută</button>
        </form>

        <div className="flex items-center gap-2 ml-auto">
          <Link to="/favorite" className="hidden sm:inline-flex relative p-2 rounded-md hover:bg-white/5" title="Favorite">
            <Heart className="w-5 h-5 text-white" />
            {favorites.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center text-white" style={{ background: "var(--amd-primary)" }}>{favorites.length}</span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden md:inline-flex items-center gap-2 p-1.5 pr-3 rounded-md hover:bg-white/5" title={user.name}>
                  <div className="w-7 h-7 rounded-full grid place-items-center text-white text-xs font-black" style={{ background: "var(--amd-primary)" }}>
                    {user.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" /> : (user.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <span className="text-sm font-semibold text-white max-w-[140px] truncate">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-56">
                <DropdownMenuItem onClick={() => navigate("/profil")}><UserCircle2 className="w-4 h-4 mr-2" /> Profilul meu</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/adauga-anunt")}><PlusCircle className="w-4 h-4 mr-2" /> Adaugă anunț</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/favorite")}><Heart className="w-4 h-4 mr-2" /> Favorite</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(adminLoggedIn ? "/admin" : "/admin/login")}><ShieldCheck className="w-4 h-4 mr-2" /> Administrare</DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }}>Ieșire din cont</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden md:inline-flex items-center gap-2 px-3 h-10 rounded-md hover:bg-white/5 text-white text-sm font-semibold" title="Conectare">
              <LogIn className="w-4 h-4" /> Conectare
            </Link>
          )}
          {adminLoggedIn && (
            <Link to="/admin" className="hidden md:inline-flex p-2 rounded-md hover:bg-white/5" title="Admin"><ShieldCheck className="w-5 h-5" style={{ color: "var(--amd-primary)" }} /></Link>
          )}
          <Button onClick={onPostAd} className="amd-btn-primary h-10 px-3 md:px-5 font-semibold gap-2">
            <PlusCircle className="w-4 h-4" /><span className="hidden sm:inline">Adaugă anunț</span><span className="sm:hidden">Anunț</span>
          </Button>
          <button className="md:hidden p-2 text-white" onClick={() => setMobileOpen((v) => !v)} aria-label="menu">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <div className="md:hidden px-3 pb-3">
        <form onSubmit={submit} className="flex items-stretch h-11 rounded-md overflow-hidden" style={{ background: "var(--amd-surface-alt)", border: "1px solid var(--amd-border)" }}>
          <div className="flex items-center gap-2 px-3 flex-1">
            <Search className="w-4 h-4" style={{ color: "var(--amd-text-muted)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ce cauți?" className="w-full bg-transparent border-0 outline-none text-sm text-white placeholder:text-neutral-500" />
          </div>
          <button type="submit" className="px-4 font-semibold text-white text-sm amd-btn-primary">Caută</button>
        </form>
      </div>

      <nav className="hidden md:block border-t" style={{ borderColor: "var(--amd-border)", background: "var(--amd-bg)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <ul className="flex items-center gap-1 overflow-x-auto py-2">
            {categories.map((c) => (
              <li key={c.id}><Link to={`/categorie/${c.id}`} className="whitespace-nowrap px-3 py-1.5 text-sm rounded-md hover:bg-white/5 amd-link">{c.name}</Link></li>
            ))}
          </ul>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden border-t fade-in" style={{ borderColor: "var(--amd-border)", background: "var(--amd-surface)" }}>
          <div className="px-3 py-3 grid gap-1">
            {user ? (
              <>
                <Link to="/profil" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-md hover:bg-white/5">
                  <UserCircle2 className="w-5 h-5" /> {user.name}
                </Link>
                <button onClick={async () => { setMobileOpen(false); await logout(); navigate("/"); }} className="flex items-center gap-3 p-3 rounded-md hover:bg-white/5 text-left">
                  Ieșire din cont
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-md hover:bg-white/5"><LogIn className="w-5 h-5" /> Conectare / Înregistrare</Link>
            )}
            <Link to="/favorite" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-md hover:bg-white/5"><Heart className="w-5 h-5" /> Favorite ({favorites.length})</Link>
            <Link to={adminLoggedIn ? "/admin" : "/admin/login"} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 p-3 rounded-md hover:bg-white/5"><User className="w-5 h-5" /> {adminLoggedIn ? "Administrare" : "Admin login"}</Link>
            <div className="h-px my-1" style={{ background: "var(--amd-border)" }} />
            {categories.map((c) => (
              <Link key={c.id} to={`/categorie/${c.id}`} onClick={() => setMobileOpen(false)} className="p-3 rounded-md hover:bg-white/5 text-sm">{c.name}</Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
